/**
 * Async semaphore that limits global concurrency across all callers.
 * Callers that exceed the limit are queued and resolved in FIFO order.
 */
export class AsyncSemaphore {
  private running = 0;
  private queue: Array<() => void> = [];

  constructor(private limit: number) {}

  public async acquire(): Promise<void> {
    if (this.running < this.limit) {
      this.running++;
      return;
    }
    return new Promise<void>((resolve) => {
      this.queue.push(resolve);
    });
  }

  public release(): void {
    if (this.running <= 0) {
      return;
    }
    if (this.queue.length > 0) {
      // Hand the slot directly to the next waiter (running count stays the same)
      const next = this.queue.shift();
      next?.();
    } else {
      this.running--;
    }
  }

  /**
   * Teardown: resets state and discards all queued waiters.
   * Pending acquire() promises will never resolve — callers should
   * use a cancellation signal to avoid relying on them.
   */
  public reset(): void {
    this.running = 0;
    this.queue = [];
  }
}
